import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { addPhoneNumber, verifyPhoneNumber, resendPhoneCode, deletePhoneNumber } from '../api/phoneNumbers'
import { getPreferences } from '../api/preferences'
import { PREFERENCES_KEY } from './usePreferences'
import type { UserPhoneNumber, Preferences } from '../types'

// Phones are part of the combined GET /api/v1/notifications response — no separate fetch.
export function usePhoneNumbers() {
  return useQuery<Preferences, Error, UserPhoneNumber[]>({
    queryKey: PREFERENCES_KEY,
    queryFn: getPreferences,
    select: (data) => data.phones,
    staleTime: 0,
  })
}

export function useAddPhoneNumber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ number, label }: { number: string; label: string }) =>
      addPhoneNumber(number, label),
    onSuccess: () => qc.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  })
}

export function useVerifyPhoneNumber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ number, code }: { number: string; code: string }) =>
      verifyPhoneNumber(number, code),
    onSuccess: (updatedPhones) => {
      // Backend returns all phones with the verified one substituted from a strongly-consistent
      // base-table read. Write directly into cache so the UI reflects the correct state immediately
      // instead of triggering a GSI refetch that may still return is_verified=false.
      qc.setQueryData<Preferences>(PREFERENCES_KEY, (old) => {
        if (!old) return old
        return { ...old, phones: updatedPhones }
      })
      // Invalidate after a short delay to let DynamoDB GSI catch up before the background refresh.
      setTimeout(() => qc.invalidateQueries({ queryKey: PREFERENCES_KEY }), 2000)
    },
  })
}

export function useResendPhoneCode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (number: string) => resendPhoneCode(number),
    onSuccess: () => qc.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  })
}

export function useDeletePhoneNumber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (number: string) => deletePhoneNumber(number),
    onSuccess: () => qc.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  })
}
